import React from "react";
import { connect, DispatchProp } from "react-redux";
import { createSelector } from "reselect";
import { pick } from "../../utils/lodash";
import { StoreState } from "../../models/states/StoreState";
import { QuestionModel } from "../../models/QuestionModel";
import { InterviewState } from "../../models/states/InterviewState";
import { __isQuestionVisible__ } from "../../expressions/parser";
import { NavigationType } from "../../models/enums/NavigationType";
import { Question } from "../../components/page/Question";
import { ValidationResultModel } from "../../models/ValidationResultModel";
import { Signal } from "../../signals";
import { SignalListener } from "../../components/shared/SignalListener";
import { ThemeModel } from "../../models/ThemeModel";
import { QuestionnaireModel } from "../../models/QuestionnaireModel";
import { DeviceModel } from "../../models/DeviceModel";
import { ValidationOptionsModel } from "../../models/ValidationOptionsModel";
import InputFocusRenderer from "../../components/renderers/v2/InputFocusRenderer";
import { isAutoAdvanceExcluded } from "../../models/utilities/questionUtilities";

interface Props {
    validateSignal: Signal<ValidationOptionsModel, ValidationResultModel>;
    onNavigation(type: NavigationType): void;
    theme: ThemeModel;
}

interface StateProps {
    questions: QuestionModel[];
    questionnaire: QuestionnaireModel;
    matrixIndex: number;
    device: DeviceModel;
    interview: InterviewState;
    language: string;
    validationByQuestion: ValidationResultModel[];
    isNavigationDisabled: boolean;
    isFormInputFocused: boolean;
}

type PropsU = Props & StateProps & DispatchProp;

class QuestionListContainerImpl extends React.PureComponent<PropsU> {
    validateSignal = new Signal<ValidationOptionsModel, ValidationResultModel>();

    componentDidUpdate(prevProps: PropsU) {
        const { questions } = this.props;
        // On page change scroll to the first question.
        if (prevProps.questions !== questions) {
            this.scrollToFirstQuestion();
        }
    }

    onValidate = (options?: ValidationOptionsModel): ValidationResultModel => {
        const results = this.validateSignal.dispatch(options);
        const initialValidationState = { isValid: true, errorsByVariableName: {} };
        return results.reduce(
            (a, b) => ({
                isValid: a.isValid && b.isValid,
                errorsByVariableName: { ...a.errorsByVariableName, ...b.errorsByVariableName },
            }),
            initialValidationState,
        );
    };

    scrollToFirstQuestion() {
        const firstQuestion = document.getElementsByClassName("questionBlock")[0];
        if (firstQuestion != null) {
            firstQuestion.scrollIntoView(true);
        }
    }

    render() {
        const {
            validateSignal,
            questions,
            dispatch,
            questionnaire,
            device,
            matrixIndex,
            interview,
            language,
            validationByQuestion,
            onNavigation,
            theme,
            isNavigationDisabled,
            isFormInputFocused,
        } = this.props;
        return (
            <main className="question-list" id="maincontent">
                <SignalListener signal={validateSignal} handler={this.onValidate} />
                {questions.map((q, i) => (
                    <Question
                        question={q}
                        dispatch={dispatch}
                        key={`${q.elementId}.row(${matrixIndex})`}
                        questionnaire={questionnaire}
                        device={device}
                        isHidden={!__isQuestionVisible__(q, interview)}
                        isSingleOnPage={
                            questions.filter((pageQuestion) => !isAutoAdvanceExcluded(pageQuestion))
                                .length === 1
                        }
                        validateSignal={this.validateSignal}
                        language={language}
                        validationResult={validationByQuestion[i]}
                        onNavigation={onNavigation}
                        isNavigationDisabled={isNavigationDisabled}
                        theme={theme}
                    />
                ))}
                <InputFocusRenderer isFormInputFocused={isFormInputFocused} />
            </main>
        );
    }
}

const getQuestions = (state: StoreState) => {
    if (!state.interview.navigation.currentPage) {
        return [];
    }
    return state.interview.navigation.currentPage.questions;
};

const getValidation = (state: StoreState) => state.interview.validation;

const getIsFormInputFocused = (state: StoreState) => state.isFormInputFocused;

const getValidationForQuestion = createSelector(
    (validation: ValidationResultModel) => validation,
    (_validation, question: QuestionModel) => question,
    (validation, question) => {
        const errorsByVariableName = pick(validation.errorsByVariableName, [
            ...question.variableNames,
            question.name,
        ]);
        return { isValid: Object.entries(errorsByVariableName).length === 0, errorsByVariableName };
    },
);

const getValidationByQuestion = createSelector(
    getValidation,
    getQuestions,
    (validation, questions) =>
        questions.map((question) => getValidationForQuestion(validation, question)),
);

const mapStateToProps = (state: StoreState, props: Props): Props & StateProps => ({
    ...props,
    questions: getQuestions(state),
    questionnaire: state.interview.questionnaire,
    matrixIndex: state.interview.navigation.currentPage.loopIndex,
    device: state.interview.device,
    interview: state.interview,
    language: state.interview.language,
    isNavigationDisabled: state.interview.navigation.disabled,
    validationByQuestion: getValidationByQuestion(state),
    isFormInputFocused: getIsFormInputFocused(state),
});

export const QuestionListContainer = connect(mapStateToProps)(QuestionListContainerImpl);
